<template>
  <ActivityPropertyPageHeader/>
  <ActivityPropertyPageGallery/>
  <ActivityPropertyPageNavigation/>
  <div class="property-info">
    <div class="property-info__list pr-6">
      <section
          v-if="property.description"
          class="property-details property-details--description text-body-1"
      >
        <div v-if="!showFullDescription && isClient">
          {{property.description.substring(0, fourLinesCharacterLength)}}<template v-if="!isExpandedDescription">...</template>
        </div>
        <div v-else>
          {{property.description}}
        </div>
        <button
           v-if="property.description?.length > fourLinesCharacterLength"
           @click="isExpandedDescription = !isExpandedDescription"
            class="btn btn-link show-more-link"
           aria-label="Toggle full description"
        >
          {{$t('Show')}} {{!isExpandedDescription ? 'More' : 'Less'}}
        </button>
      </section>
      <section id="PropertyHighlights" class="property-details block-split" v-if="highlightsList.length">
        <h2 class="text-title-2 fw-semi-bold">{{$t('Highlights')}}</h2>
        <div class="container">
          <ul class="activity-bullets activity-bullets--dot">
            <li v-for="(item, index) in highlightsList" :key="index">{{ item }}</li>
          </ul>
        </div>
      </section>
      <EntranceTicket />
      <LazyActivityPropertyPageFacilities class="property-details block-split"/>

      <section id="PropertyIncluded" class="property-details block-split" v-if="inclusionsList.length || exclusionsList.length">
        <h2 class="text-title-2 fw-semi-bold">{{$t("What's included")}}</h2>
        <div class="container">
          <ul class="activity-bullets activity-bullets--yes" v-if="inclusionsList.length">
            <li v-for="(item, index) in inclusionsList" :key="index">{{ item }}</li>
          </ul>
          <template v-if="exclusionsList.length">
            <h3 class="text-body-1 fw-semi-bold activity-included__subhead">{{$t('Not included')}}</h3>
            <ul class="activity-bullets activity-bullets--no">
              <li v-for="(item, index) in exclusionsList" :key="index">{{ item }}</li>
            </ul>
          </template>
        </div>
      </section>

      <section id="PropertyMeetingPoint" class="property-details block-split" v-if="property.departure_point">
        <h2 class="text-title-2 fw-semi-bold">{{$t("Where you'll meet")}}</h2>
        <div class="container text-body-1">
          {{ property.departure_point }}
        </div>
      </section>

      <section id="PropertyOperator" class="property-details block-split" v-if="property.vendor_owner">
        <h2 class="text-title-2 fw-semi-bold">{{$t('Operated by')}}</h2>
        <div class="container text-body-1">
          {{ property.vendor_owner }}
        </div>
      </section>

      <section class="property-details property-details--important block-split" v-if="property.important_info">
        <h2 class="text-title-2 fw-semi-bold policies-sub-header">{{$t('Important information')}}</h2>
        <div class="container policies-sub-body">
          {{property.important_info}}
        </div>
      </section>
      <section id="PropertyLocation" class="property-details property-details--location block-split">
        <h2 class="text-title-2 fw-semi-bold">{{$t('Location')}}</h2>
        <div class="container">
          <div class="map-container">
            <client-only>
              <LazyMapView :lat="property.geo_place?.latitude" :lon="property.geo_place?.longitude" v-if="isClient && property.geo_place"/>
            </client-only>
          </div>
          <div class="pt-3 text-body-1 fw-semi-bold">
            {{property.address}}
          </div>
        </div>
      </section>

      <section id="PropertyFAQ" class="property-details block-split" v-if="faqList.length">
        <h2 class="text-title-2 fw-semi-bold">FAQ</h2>
        <div class="container">
          <ExpandablePanel
            v-for="(faqItem, index) in faqList"
            :openByDefault="index === 0"
            :key="index"
            :title="faqItem.title"
            :text="faqItem.text"
          />
        </div>
      </section>

      <section id="PropertyReview" class="property-details block-split" v-if="property.avg_rating > 0">
        <h2 class="text-title-2 fw-semi-bold">{{$t('Reviews')}}</h2>
        <div class="container">
          <div class="reviews-summary">
            <div class="reviews-summary__score">
              <div class="reviews-score-badge">{{ Number(property.avg_rating).toFixed(1) }}</div>
              <div class="reviews-score-label">
                <span class="reviews-score-text" v-if="property.avg_rating >= 4.5">{{$t('Excellent')}}</span>
                <span class="reviews-score-text" v-else-if="property.avg_rating >= 3.5">{{$t('Very Good')}}</span>
                <span class="reviews-score-text" v-else-if="property.avg_rating >= 3">{{$t('Good')}}</span>
                <span class="reviews-score-text" v-else>{{$t('Fair')}}</span>
                <span class="reviews-count" v-if="reviewsCount">
                  {{ reviewsCount.toLocaleString() }} {{ reviewsCount === 1 ? $t('review') : $t('reviews') }}
                </span>
                <span class="reviews-count" v-else-if="property.overall_rating">
                  {{ Number(property.overall_rating).toFixed(1) }} / 5
                </span>
              </div>
            </div>
            <p class="reviews-attribution" v-if="reviewsCount">
              {{ $t('Rating and review count provided by the activity supplier') }}
            </p>
            <p class="reviews-attribution" v-else>
              {{ $t('Total review count and overall rating based on Viator and Tripadvisor reviews') }}
            </p>
          </div>
        </div>
      </section>

      <section class="property-details block-split related-activities" v-if="relatedActivities.length">
        <h2 class="text-title-2 fw-semi-bold">{{$t('You might also like')}}</h2>
        <div class="container">
          <div class="related-grid">
            <NuxtLink
              v-for="related in relatedActivities"
              :key="related.id"
              :to="localePath(`/experiences/${(property.geo_place?.slug && property.geo_place.slug !== 'null') ? property.geo_place.slug : 'results'}/${related.id}-${related.slug}`)"
              class="related-card"
            >
              <div class="related-card__image">
                <img
                  v-if="related.cover_image_url"
                  :src="imageUrlWithProxy(related.cover_image_url, 300, 200, 'fill')"
                  :alt="related.normalized_name || related.name"
                  loading="lazy"
                />
                <PhotoPlaceholder v-else />
              </div>
              <div class="related-card__info">
                <div class="related-card__title">{{ related.normalized_name || related.name }}</div>
                <div class="related-card__meta">
                  <span v-if="related.avg_rating > 0" class="related-card__rating">
                    <span class="related-rating-badge">{{ Number(related.avg_rating).toFixed(1) }}</span>
                  </span>
                  <span v-if="related.duration" class="related-card__duration">{{ formatDuration(related.duration) }}</span>
                </div>
                <div class="related-card__price" v-if="related.price_from">
                  {{$t('From')}} <strong>{{ related.price_from.formatted }}</strong>
                </div>
              </div>
            </NuxtLink>
          </div>
        </div>
      </section>

    </div>
    <div class="property-info__booking">
      <client-only>
        <ActivityPropertyPageBookingBlock ref="bookingBlock" @on-availability-update="onAvailabilityUpdate"/>
      </client-only>
    </div>
  </div>

</template>
<script setup lang="ts">
import { usePropertyStore } from '~/store/property'
import { useActivityPropertyStore } from "~/store/activityProperty";
import ExpandablePanel from '~/components/ExpandablePanel.vue'
import { useRoute } from "vue-router";
import EntranceTicket from "~/components/ActivityPropertyPage/EntranceTicket.vue";
import { imageUrlWithProxy } from "~/utils/imageproxy";
import { toBulletList } from "~/utils/activityText";
import dayjs from '~/utils/dayjs'

const activityPropertyStore = useActivityPropertyStore();
const property = activityPropertyStore.property

// Suppliers send these as one period-joined string; toBulletList turns them back into a list.
const highlightsList = computed(() => toBulletList(property.highlights))
const inclusionsList = computed(() => toBulletList(property.inclusions))
const exclusionsList = computed(() => toBulletList(property.exclusions))
const reviewsCount = computed(() => Number(property.supplier_reviews_count) || 0)

const emit = defineEmits(['onAvailabilityUpdate'])
const bookingBlock = ref()
const isExpandedDescription = ref(false)
let _windowWidth = ref(window?.innerWidth || 0)
const isClient = ref(false);
let faqList: any[] = []
const config = useRuntimeConfig()
const configData = config.public
const route = useRoute()
const { gtag } = useGtag()
const localePath = useLocalePath()
const relatedActivities = ref<any[]>([])

const texts = [{
  brand_name: 'Tripening'
}]

const availabilitiesList = [
  {code: 'adults', label: 'Adults', age: '18+'},
  {code: 'children', label: 'Children', age: '0-17'},
]

const availabilityModel = ref<any>({
  adults: 2,
  children: 0,
  ages: []
})

const maxChildAge = 17
let availableChildAgesList = Array.from(Array(maxChildAge + 1)).map((item, index) => ({value: index, label: String(index)}))

function checkChildrenAges() {
  const diff = availabilityModel.value['children'] - availabilityModel.value.ages.length
  if (diff > 0) {
    availabilityModel.value.ages.push(...Array.from(Array(diff)).map(() => 0))
  } else if (diff < 0) {
    availabilityModel.value.ages.splice(availabilityModel.value.ages.length + diff, -diff)
  }
}

function updateAvailability(code: string, diff: number) {
  if ((availabilityModel.value[code] + diff) >= 0) {
    availabilityModel.value[code] += diff
  }
  checkChildrenAges()
}

function formatDuration(seconds: number) {
  return dayjs.duration(seconds * 1000).humanize()
}

//add event listener to window resize
onMounted(async () => {
  gtag('event', 'page_view', {
    affiliation: 'Tripening',
    items: [
      {
        item_name: property.name || '',
        item_id: property.id || '',
      }
    ]
  })

  isClient.value = true;
  window.addEventListener('resize', handleResize)
  createFAQList()

  // Load related activities
  if (property.geo_place?.id) {
    try {
      const Cookies = (await import('js-cookie')).default
      const response: any = await $fetch(configData.baseURL + '/api/product/v3/activities', {
        params: {
          place_id: property.geo_place.id,
          size: 4,
          sort_by: 'rating_desc'
        },
        headers: {
          'X-TRIPENING-CONTEXT-ID': configData.brand || 'tripening',
          'X-TRIPENING-CURRENCY': Cookies.get('currency') || 'USD'
        }
      })
      if (response?.activities) {
        relatedActivities.value = response.activities.filter((a: any) => a.id !== property.id).slice(0, 3)
      }
    } catch (e) {
      // silent fail for related activities
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

function handleResize() {
  _windowWidth.value = window.innerWidth
}

//computed
const siteHost = computed(() => {
  return configData.siteName || ''
})

const cheapestRate = computed(() => {
  return usePropertyStore()?.rooms[0]?.rates[0]
})

let isMobile = computed(() => _windowWidth.value && _windowWidth.value <= 430)

let showFullDescription = computed(() => {
  const _isExpDescription = isExpandedDescription?.value
  const _descriptionLength = property?.description?.length || 0
  const isShortDescription = _descriptionLength < fourLinesCharacterLength.value
  return (isShortDescription|| _isExpDescription) || !isMobile.value
})

const amenitiesList = computed(() => {
  if (!property.amenities?.length) {
    return []
  }
  const temp = [...property.amenities.filter((amenity: any) => amenity.group === 'hotel')]
  temp.sort((a: any, b: any) => a.code - b.code)
  return temp.splice(0, 11)
})

const fourLinesCharacterLength = computed(() => {
  if (!_windowWidth.value || !isMobile) return 0

  const AVERAGE_FONT_SET_WIDTH_PX = 10; //for font size 16 px
  const screenWidth = _windowWidth.value;
  const averageCharsPerLine = screenWidth / AVERAGE_FONT_SET_WIDTH_PX;
  return averageCharsPerLine * 4;
});

const isDescriptionLessThanFourLines = computed(() => {
  if (!window) return  false

  const AVERAGE_FONT_SET_WIDTH_PX = 10; //for font size 16 px
  const screenWidth = window?.innerWidth;
  const averageCharsPerLine = screenWidth / AVERAGE_FONT_SET_WIDTH_PX;

  return property.description.length < averageCharsPerLine && screenWidth < 768;
});

interface FaqItem {
  title: string;
  text: string;
}

//methods
function onAvailabilityUpdate() {
  emit('onAvailabilityUpdate')
}

function createFAQList() {
  //create FAQList
  if (property.location) {
    faqList.push(createFaqItem(property, "Where is {hotel_name} located?", 'Located in ' + property.location.city_name))
  }
  if (isPetsFriendly()) {
    faqList.push(createFaqItem(property, "Is {hotel_name} pet-friendly?", "Yes, {hotel_name} is pet-friendly."))
  }

  if (property?.location_info) {
    faqList.push(createFaqItem(property, "How many rooms does {hotel_name} have?", `{hotel_name} has ${property?.location_info.rooms} rooms.`))

    if (property?.location_info.floors) {
      faqList.push(createFaqItem(property, "How many floors does {hotel_name} have?", `{hotel_name} has ${property?.location_info.floors} floors.`))
    }
  }

  if (property.payment_method) {
    faqList.push(createFaqItem(property, "What payment cards are accepted at {hotel_name}?", `The following payment methods are accepted at {hotel_name}: ${setAvailablePaymentMethodsString()}.`))
  }

  if (property.sport_facilities) {
    const regex = /fitness/i;
    const facility = property.sport_facilities.find((facility: any) => facility.code === 0 || regex.test(facility.description));
    if (facility) {
      faqList.push(createFaqItem(property, "Does {hotel_name} have a fitness center?", "Yes, {hotel_name} has a fitness center."));
    }
  }

}

function setAvailablePaymentMethodsString() {
  const PATMENT_METHODS_NAMES: any = {
    "american_express": 'American Express',
    "diners_club": 'Diners Club',
    "ec": 'EC-Card',
    "eurocard": 'Eurocard',
    "euro_600": 'Euro6000',
    "jcb": 'JCB',
    "mastercard": 'Mastercard',
    "maestro": 'Maestro',
    "visa": 'Visa',
    "visa_electron": 'Visa Electron',
  }

  const allPaymentMethods =  Object.keys(property.payment_method)
  const allAvailablePaymentMethods = allPaymentMethods.filter((m: any) => property.payment_method[m])
  const allAvailablePaymentMethodsNames = allAvailablePaymentMethods.map((m: any) => PATMENT_METHODS_NAMES[m])

  return allAvailablePaymentMethodsNames.join(', ')
}

//create FAQ Item
function isPetsFriendly() {
  const regex = /pets/i

  return checkStringInAmenities(regex)
}

function checkStringInAmenities(regex: any) {
  return amenitiesList?.value.some((amenity: any) =>
      Object.values(amenity).some(value =>
          typeof value === 'string' && regex.test(value)
      )
  )
}

function createFaqItem(hotel: any, template: any, textTemplate: any): FaqItem {
  let title = '',
      text = ''
  const hotelName = hotel.name;
  const regex = /\{hotel_name\}/;

  title = template.replace(regex, hotelName);
  text = textTemplate.replace(regex, hotelName);

  return {title, text}
}

</script>

<style lang="scss">
.property-info {
  display: flex;
  max-width: 100%;

  //tablet
  @media screen and (max-width: 1024px) and (min-width: 768px){
    justify-content: space-between;
  }

  .property-info__list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 28px;

    @media screen and (max-width: 1024px) and (min-width: 769px) {
      max-width: 608px;
    }

    //vertical tablet
    @media screen and (max-width: 768px) and (min-width: 430px){
      gap: 50px;
    }

    &.pr-6 {
      @media screen and (max-width: 1024px) and (min-width: 769px) {
        padding-right: 0;
      }

    }

    .property-details {
      padding-bottom: 32px;

      @media screen and (max-width: 1024px) {
        padding-bottom: 6px;
        border-bottom: none;
      }

      &.block-split {
        display: flex;
        gap: 24px;
        align-items: flex-start;

        //vertical tablet
        @media screen and (max-width: 768px) and (min-width: 430px){
          flex-direction: column;
        }

        & > h2 {
          width: 167px;
          min-width: 167px;

          @media screen and (max-width: 1024px) and (min-width: 769px){
            width: auto;
            max-width: 132px;
            min-width: 132px;
          }

          @media screen and (max-width: 768px) and (min-width: 430px) {
            max-width: 100%;
          }
        }
        .container {
          flex: 1;
        }
      }
      &.pt-8 {

        //vertical tablet
        @media screen and (max-width: 768px) and (min-width: 431px){
          padding-top: 37px;
        }

        @media screen and (max-width: 430px) {
          padding-top: 24px;
        }
      }

      &--rooms {
        padding-top: 0;
        position: relative;

        .p-dialog-mask {
          position: absolute !important;
          align-items: flex-start !important;
        }

        // The sold-out panel's mask IS the grey placeholder box that stands in for the room
        // list, so centre the panel in it. Top-pinning is right for the room drawers above,
        // which open over a full list, but here it left a band of grey under the panel.
        .sold-out-rooms .p-dialog-mask {
          align-items: center !important;
        }

        &.block-split {
          //vertical tablet
          @media screen and (max-width: 768px) and (min-width: 430px){
            gap: 1px;
          }
        }
      }

      &--description {
        padding-top: 24px;

        @media screen and (max-width: 768px) {
          padding-bottom: 0;
          border-bottom: none;
        }
      }

      &--policies {

        &.block-split {
          //vertical tablet
          @media screen and (max-width: 768px) and (min-width: 430px){
            gap: 0;
          }

          h2.text-title-2 {
            @media screen and (max-width: 768px) {
              padding-bottom: 10px;
            }
          }

          .pt-1, .pt-5 {
            @media screen and (max-width: 768px) {
              padding-top: 0;
            }
          }
        }

        .policies-sub-header {
          font-size: 16px;
          color: var(--black);

          //tablet
          @media screen and (max-width: 1024px) and (min-width: 769px){
            font-size: 18px;
          }

          //vertical tablet
          @media screen and (max-width: 768px) and (min-width: 431px){
            font-size: 16px;
          }

          @media screen and (max-width: 430px) {
            font-size: 16px;
            text-transform: capitalize;
          }

          &.pt-5 {
            //tablet
            @media screen and (max-width: 1024px) and (min-width: 768px){
              padding-top: 0.7rem;
            }

            //vertical tablet
            @media screen and (max-width: 768px) and (min-width: 431px){
              padding-top: .2rem;
            }
          }

          & + .pt-1 {
            @media screen and (max-width: 1024px) and (min-width: 768px){
              padding-top: 0;
            }
          }
        }

        .policies-sub-body {
          color: var(--dark-grey);
          font-size: 14px;
          font-weight: 400;
          line-height: 142.857% ;
        }
      }

      &--location {
        &.block-split {

          //vertical tablet
          @media screen and (max-width: 976px) and (min-width: 340px){
            gap: 12px;
          }

          h2.text-title-2 {
            padding-bottom: 12px;
            //vertical tablet
            @media screen and (max-width: 768px) and (min-width: 430px){
              padding-bottom: 0;
            }
          }

          .btn {
            //mobile
            @media screen and (max-width: 430px) {
              width: 100%;
            }
          }

          .property-location-description {
            color: var(--dark-grey);
            font-size: 14px;
            font-weight: 400;
            line-height: 142.857%
          }
        }
      }

      &--important {
        &.block-split {
          //vertical tablet
          @media screen and (max-width: 768px) and (min-width: 430px){
            gap: 13px;
          }
        }
      }

      .show-more-link {
        margin-top: 10px;
        display: none;

        @media screen and (max-width: 430px) {
          display: block;
        }
      }
    }
  }
  .property-info__booking {
    width: 357px;
    position: relative;
  }
  @media screen and (max-width: 1024px) and (min-width: 976px){
    .property-info__booking {
      max-width: 292px;
      position: relative;
    }
  }

  //vertical tablet
  @media screen and (max-width: 975px) and (min-width: 431px){
    .property-info__booking {
      max-width: 264px;
    }
  }

  @media screen and (max-width: 430px) {
    display: block;
    .property-info__booking {
      position: fixed;
      bottom: 0;
      right: 0;
      left: 0;
      width: 100%;
      z-index: 2;
    }
    .property-info__list {
      padding-right: 0;
      padding-bottom: 80px;
      .property-details {
        &.block-split {
          display: block;
          h2 {
            padding-bottom: 20px;
            width: 100%;
            max-width: 100%;
          }
        }
      }
    }
  }
}
.map-container {
  width: 100%;
  background-color: var(--light-grey);
  position: relative;
  z-index: 1;
  img {
    border-radius: 8px;
  }
}

/* Reviews section */
.reviews-summary {
  display: flex;
  gap: 32px;
  align-items: flex-start;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
}

.reviews-summary__score {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reviews-score-badge {
  background: var(--green);
  color: var(--white);
  font-size: 16px;
  font-weight: 600;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 24px;
}

.reviews-score-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reviews-score-text {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--black);
}

.reviews-count {
  font-size: 14px;
  color: var(--dark-grey);
}

.reviews-attribution {
  margin-top: 8px;
  font-size: 12px;
  color: var(--dark-grey);
}

/* Related activities */
.related-activities.block-split {
  display: block !important;

  & > h2 {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.related-card {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 0 2px var(--grey-border);
  transition: box-shadow 0.2s, transform 0.2s;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  &__image {
    height: 140px;
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__info {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--black);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--dark-grey);
  }

  &__rating {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__price {
    font-size: 14px;
    color: var(--black);
    margin-top: auto;
  }
}

.related-rating-badge {
  background: var(--green);
  color: var(--white);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

/* Supplier content lists: highlights, what's included and what isn't.
   This style block is global (no `scoped`), so these names are deliberately specific. */
.activity-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    position: relative;
    padding-left: 26px;
    line-height: 1.5;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 3px;
      width: 16px;
      height: 16px;
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }
  }

  &.activity-bullets--yes li::before {
    background-image: url('/images/check-one.svg');
  }

  &.activity-bullets--no li::before {
    background-image: url('/images/cancel-items.svg');
  }

  &.activity-bullets--dot li::before {
    content: '\2022';
    top: 0;
    color: var(--blue);
    font-size: 20px;
    line-height: 1.2;
  }
}

.activity-included__subhead {
  padding-top: 20px;
  padding-bottom: 8px;
}
</style>
